import React, { createContext, useContext, useState, useEffect } from "react";
import { getRefreshFeatureList } from "../api";
import { CookieUtils } from "../axios.config";

const RoleFeatureContext = createContext();

export const RoleFeatureProvider = ({ children }) => {
  const [roleFeatureList, setRoleFeatureListState] = useState(null);

  useEffect(() => {
    refreshFeatureList();
  }, []);

  useEffect(() => {
    const handleStorageChange = (event) => {
      if (event.key === "roleFeatureList" && event.newValue) {
        const updatedFeatures = JSON.parse(event.newValue);
        setRoleFeatureList(updatedFeatures);
      }
    };

    window.addEventListener("storage", handleStorageChange);
    return () => window.removeEventListener("storage", handleStorageChange);
  }, []);

  const refreshFeatureList = async () => {
    try {
      const token = CookieUtils.get("token");
      if (!token?.access_token) return;

      const headers = {
        headers: {
          Authorization: `Bearer ${token.access_token}`,
        },
      };

      const response = await getRefreshFeatureList(headers);
      if (response.status === 200 && response.data?.data) {
        setRoleFeatureList(response.data.data);
        CookieUtils.set("roleFeatureList", response.data.data, 1);
      }
    } catch (error) {
      console.error("Failed to fetch feature list:", error);
    }
  };

  const setRoleFeatureList = (roleFeatureData) => {
    setRoleFeatureListState(roleFeatureData);
    CookieUtils.set("roleFeatureList", roleFeatureData, 1);
  };

  const organizeFeaturesBySource = (features) => {
    const grouped = {};
    features.forEach(({ source, featureList }) => {
      grouped[source] = featureList.map((feature) => ({
        ...feature,
        children: feature.subFeatures || [],
      }));
    });
    return grouped;
  };

  const hasAccess = (componentName, accessType = "readAccess") => {
    if (!roleFeatureList?.features) return false;

    return roleFeatureList.features.some((source) =>
      source.featureList.some(
        (feature) =>
          (feature.componentName === componentName && feature[accessType]) ||
          feature.subFeatures?.some(
            (sub) => sub.componentName === componentName && sub[accessType]
          )
      )
    );
  };

  const getStructuredFeatures = () => {
    if (!roleFeatureList?.features) return {};
    return organizeFeaturesBySource(roleFeatureList.features);
  };

  const roleTitle = roleFeatureList?.roleTitle || null;

  return (
    <RoleFeatureContext.Provider
      value={{
        roleFeatureList,
        setRoleFeatureList,
        roleTitle,
        hasAccess,
        getStructuredFeatures,
      }}
    >
      {children}
    </RoleFeatureContext.Provider>
  );
};

export const useRoleFeature = () => useContext(RoleFeatureContext);
